import axios from 'axios';
import { APP_CONFIG } from '../config/appConfig';
import {
  Monitor, MonitorGroup, MonitorCheck, MonitorEvent, UptimeBar,
  Summary, Recipient, User, AuditLog, AuthResponse,
} from '../types';

const api = axios.create({
  baseURL: APP_CONFIG.API_URL,
  headers: { 'Content-Type': 'application/json' },
});

const clearSessionAndRedirect = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_timezone');
  localStorage.removeItem('auth_last_activity_at');
  if (!window.location.pathname.includes('/admin/login')) {
    window.location.href = '/admin/login';
  }
};

api.interceptors.request.use((config) => {
  const store = window.localStorage;
  const authToken = store.getItem('auth_token');
  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
  return config;
});

api.interceptors.response.use(
  (response) => {
    const refreshedToken = response.headers?.['x-auth-token'];
    if (refreshedToken) {
      localStorage.setItem('auth_token', refreshedToken);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      clearSessionAndRedirect();
    }
    return Promise.reject(error);
  }
);

export const monitorApi = {
  // ── Public status ──
  getSummary: () => api.get<Summary>('/status/summary'),
  getMonitors: () => api.get<Monitor[]>('/status/monitors'),
  getMonitor: (id: number) => api.get<Monitor>(`/status/monitors/${id}`),
  getMonitorChecks: (id: number, limit = 100) => api.get<MonitorCheck[]>(`/status/monitors/${id}/checks`, { params: { limit } }),
  getMonitorEvents: (id: number) => api.get<MonitorEvent[]>(`/status/monitors/${id}/events`),
  getMonitorUptimeBars: (id: number, days = 30) => api.get<UptimeBar[]>(`/status/monitors/${id}/uptime-bars`, { params: { days } }),
  getRecentEvents: (limit = 100) => api.get<MonitorEvent[]>('/status/events', { params: { limit } }),
  getGroups: () => api.get<MonitorGroup[]>('/status/groups'),

  // ── Auth ──
  login: (username: string, password: string, timezone?: string) =>
    api.post<AuthResponse>('/admin/login', { username, password, timezone }),

  // ── Admin: monitors ──
  adminGetMonitors: () => api.get<Monitor[]>('/admin/monitors'),
  createMonitor: (data: Partial<Monitor> & { recipient_ids?: number[] }) => api.post<Monitor>('/admin/monitors', data),
  updateMonitor: (id: number, data: Partial<Monitor>) => api.put<Monitor>(`/admin/monitors/${id}`, data),
  deleteMonitor: (id: number) => api.delete(`/admin/monitors/${id}`),
  toggleMonitor: (id: number) => api.post(`/admin/monitors/${id}/toggle`),
  checkMonitorNow: (id: number) => api.post<Monitor>(`/admin/monitors/${id}/check`),
  getMonitorRecipients: (id: number) => api.get<number[]>(`/admin/monitors/${id}/recipients`),
  setMonitorRecipients: (id: number, recipient_ids: number[]) => api.put(`/admin/monitors/${id}/recipients`, { recipient_ids }),

  // ── Admin: groups ──
  getAdminGroups: () => api.get<MonitorGroup[]>('/admin/groups'),
  getGroupDefaultRecipients: (id: number) => api.get<number[]>(`/admin/groups/${id}/default-recipients`),
  setGroupDefaultRecipients: (id: number, recipient_ids: number[]) => api.put(`/admin/groups/${id}/default-recipients`, { recipient_ids }),
  createGroup: (data: Partial<MonitorGroup>) => api.post<MonitorGroup>('/admin/groups', data),
  updateGroup: (id: number, data: Partial<MonitorGroup>) => api.put<MonitorGroup>(`/admin/groups/${id}`, data),
  deleteGroup: (id: number) => api.delete(`/admin/groups/${id}`),

  // ── Admin: recipients ──
  getRecipients: () => api.get<Recipient[]>('/admin/recipients'),
  addRecipient: (data: { email: string; scope: string; group_id?: number | null }) => api.post<Recipient>('/admin/recipients', data),
  updateRecipient: (id: number, data: { email?: string; scope?: string; group_id?: number | null }) => api.put<Recipient>(`/admin/recipients/${id}`, data),
  deleteRecipient: (id: number) => api.delete(`/admin/recipients/${id}`),

  // ── Admin: users ──
  getUsers: () => api.get<User[]>('/admin/users'),
  createUser: (data: { username: string; password: string; role?: string }) => api.post('/admin/users', data),
  changePassword: (id: number, new_password: string) => api.put(`/admin/users/${id}/password`, { new_password }),
  deleteUser: (id: number) => api.delete(`/admin/users/${id}`),

  // ── Admin: audit ──
  getAuditLogs: () => api.get<AuditLog[]>('/admin/audit-logs'),
};

export default api;
